/**
 * What the feed has delivered for a fixture, in three states (`.m-marketsbadge`
 * in the prototype's modals.css; §6 and `03-catalogue/README.md` describe it in
 * prose).
 *
 * The state is derived, not passed. A count of zero is `empty` however it was
 * asked for, and `queued` only shows while there is genuinely nothing yet — the
 * prototype's own `MarketsBadge` does the same coercion, and it stops a caller
 * rendering "Queued to sync" over a fixture that has twelve outcomes.
 *
 * > **Colours here follow the stylesheet, not the prose.** §6 and the catalogue
 * > README both say `ok` is accent-soft, `queued` amber and `empty` muted. The
 * > stylesheet — and every screen that renders it — has `ok` in success green,
 * > `queued` in the accent wash, and `empty` in amber. Structure and copy agree;
 * > only the colour assignment is shuffled, and it reads as a prose slip: amber
 * > for "nothing here" and green for "all present" is the sense the rest of the
 * > design uses. Logged in docs/design/admin-ui/prototype/README.md.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { RefreshIcon } from './line-icons';

interface MarketBadgeProps {
  marketCount: number;
  outcomeCount?: number;
  /** The fixture is waiting on a feed sync. Ignored once markets have arrived. */
  queued?: boolean;
}

const badgeSx = (theme: Theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 10px',
  borderRadius: `${theme.tokens.radius.control}px`,
  border: `${theme.tokens.border.width} solid`,
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
});

export const MarketBadge = ({ marketCount, outcomeCount, queued = false }: MarketBadgeProps) => {
  if (marketCount > 0) {
    return (
      <Box
        component="span"
        sx={(theme: Theme) => ({
          ...badgeSx(theme),
          backgroundColor: theme.tokens.color.successSoft,
          color: theme.tokens.componentColor.marketBadgeOkText,
          borderColor: theme.tokens.status.live.border,
        })}
      >
        {/* One inline run rather than a flex item per part: flex `gap` would
            space them visually while leaving the accessible text as
            "3markets· 12 outcomes". The spaces here are real. */}
        <span>
          <Box
            component="span"
            sx={(theme: Theme) => ({ fontFamily: theme.tokens.font.mono, fontWeight: 600 })}
          >
            {marketCount}
          </Box>{' '}
          {marketCount === 1 ? 'market' : 'markets'}
          {outcomeCount === undefined ? null : (
            <Box
              component="span"
              sx={(theme: Theme) => ({
                color: theme.tokens.componentColor.marketBadgeOkSubText,
                fontWeight: 400,
              })}
            >
              {' · '}
              {outcomeCount} {outcomeCount === 1 ? 'outcome' : 'outcomes'}
            </Box>
          )}
        </span>
      </Box>
    );
  }

  if (queued) {
    return (
      <Box
        component="span"
        sx={(theme: Theme) => ({
          ...badgeSx(theme),
          backgroundColor: theme.tokens.color.accentSoft,
          color: theme.tokens.color.accentStrong,
          borderColor: theme.tokens.status.scheduled.border,
        })}
      >
        {/* Spins while the sync is outstanding — the one piece of motion that
            says this state will resolve on its own. */}
        <RefreshIcon sx={{ fontSize: '13px', animation: 'var(--animation-spin)', flexShrink: 0 }} />
        Queued to sync
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={(theme: Theme) => ({
        ...badgeSx(theme),
        backgroundColor: theme.tokens.color.warnSoft,
        color: theme.tokens.componentColor.marketBadgeEmptyText,
        borderColor: theme.tokens.componentColor.marketBadgeEmptyBorder,
      })}
    >
      No markets
    </Box>
  );
};
